import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { Separator } from "@/app/shared/ui/separator";
import { passkeyService } from "@groot/shell/services/passkey";
import { useAuthStore } from "@groot/shell/store/auth";
import { Fingerprint } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasskeySupported, setIsPasskeySupported] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const { login, loginWithPasskey, isLoading, error } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if passkeys are supported
    const checkPasskeySupport = async () => {
      const supported = await passkeyService.isPlatformAuthenticatorAvailable();
      setIsPasskeySupported(supported);
    };
    checkPasskeySupport();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    try {
      await login(email, password);
      navigate("/");
    } catch {
      // Error is already handled in the store and displayed in the UI
    }
  };

  const handlePasskeyLogin = async () => {
    setIsPasskeyLoading(true);
    try {
      await loginWithPasskey(email || undefined);
      navigate("/");
    } catch {
      // Error is already handled in the store and displayed in the UI
    } finally {
      setIsPasskeyLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted">
      <div className="w-full max-w-md border border-border rounded-lg overflow-hidden bg-background">
        <div className="border-b border-border bg-muted px-6 py-4">
          <h1 className="text-xl font-medium text-foreground">Login</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Enter your credentials to access Nesin's Finance
          </p>
        </div>
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={isLoading}
              />
            </div>

            {error && <div className="text-sm text-destructive">{error}</div>}

            <Button type="submit" className="w-full" disabled={isLoading || isPasskeyLoading}>
              {isLoading ? "Signing in..." : "Sign in with Password"}
            </Button>
          </form>

          {isPasskeySupported && (
            <>
              <div className="relative my-6">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-2 text-xs text-muted-foreground">
                  Or
                </span>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handlePasskeyLogin}
                disabled={isLoading || isPasskeyLoading}
              >
                <Fingerprint className="size-4" />
                {isPasskeyLoading ? "Authenticating..." : "Sign in with Passkey"}
              </Button>

              {email && (
                <p className="mt-2 text-xs text-muted-foreground text-center">
                  Passkey authentication for {email}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
